import { motion } from "framer-motion";
import { Gamepad2, Zap } from "lucide-react";
import GameArena from "./GameArena";
import { openWebGame, pwaLabels } from "./DownloadCTA";
import { useLang, STR } from "@/i18n";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { lang } = useLang();
  const t = STR[lang];

  return (
    <section className="relative flex min-h-[100dvh] items-center overflow-hidden noise-overlay pt-28 pb-20 sm:pt-32 sm:pb-24">
      <div className="hero-grid-bg absolute inset-0 pointer-events-none" />
      <div className="pointer-events-none absolute right-[-18%] top-[18%] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(255,122,26,0.22),transparent_62%)]" />
      <div className="pointer-events-none absolute bottom-[-18%] left-[18%] h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(124,44,255,0.26),transparent_66%)]" />
      <span className="spark-star left-[7%] top-[18%]" style={{ "--star-size": "18px" }} />
      <span className="spark-star right-[11%] top-[22%]" style={{ "--star-size": "12px", animationDelay: "0.8s" }} />
      <span className="spark-star bottom-[18%] left-[16%]" style={{ "--star-size": "14px", animationDelay: "1.3s" }} />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-8 inline-flex items-center gap-3 text-amber-100"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[linear-gradient(145deg,#ffce55,#ff7a1a)] text-[#220900] shadow-[0_12px_28px_-18px_rgba(255,122,26,0.9)]">
              <Zap className="h-3.5 w-3.5" strokeWidth={2.6} />
            </span>
            <span className="mono text-[11px] font-bold uppercase tracking-[0.18em] text-white/68">
              {t.hero_pill}
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-black leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            <Line delay={0.25}>{t.hero_l1}</Line>
            <Line delay={0.37}>{t.hero_l2}</Line>
            <Line delay={0.49}>
              <span className="text-[#ffcf68] drop-shadow-[0_12px_28px_rgba(255,122,26,0.22)]">
                {t.hero_l3}
              </span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/64 sm:text-lg"
          >
            {t.hero_sub} <span className="font-semibold text-white">{t.hero_tagline}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex items-center gap-4"
          >
            <button
              onClick={openWebGame}
              data-testid="hero-open-game-btn"
              className="game-sheen group flex min-h-[66px] items-center gap-3 overflow-hidden rounded-[22px] bg-lime-300 px-6 py-3.5 text-[#071018] shadow-[0_18px_42px_-18px_rgba(190,242,100,0.55)] transition duration-300 hover:-translate-y-0.5 hover:bg-lime-200 active:translate-y-0 active:scale-[0.98]"
            >
              <Gamepad2 className="h-7 w-7" strokeWidth={2.4} />
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-bold uppercase text-[#25310f]/70">
                  {pwaLabels[lang]?.openOverline || pwaLabels.pt.openOverline}
                </span>
                <span className="block text-base font-extrabold">
                  {pwaLabels[lang]?.openButton || pwaLabels.pt.openButton}
                </span>
              </span>
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex items-center gap-8"
          >
            {[
              ["60s", t.stat_60s],
              [{ pt: "Prêmios", en: "Prizes", es: "Premios" }[lang], t.stat_pix],
              ["+18", t.stat_age],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-display text-xl font-bold text-white sm:text-2xl">{v}</p>
                <p className="mono mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/42">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:pl-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 flex justify-center"
          >
            <img
              src="/logo.png"
              alt="DUELO X contra O"
              data-testid="hero-logo"
              className="animate-float-slow -mt-5 mb-8 w-44 drop-shadow-[0_18px_42px_rgba(255,191,61,0.32)] sm:w-52"
            />
          </motion.div>
          <GameArena />
        </div>
      </div>
    </section>
  );
}
